/**
 * The main view.
 *
 * The pillars-of-support metaphor, drawn literally: a slab resting on columns. The regime
 * is the slab. Each institution is a column whose remaining loyalty is how much of it is
 * still solid. Below, the sectors — nodes that fill with ink as people walk out — with
 * lines running up to the institutions they are socially embedded in, thickening as the
 * pressure travels.
 *
 * When a column goes it cracks, tips, and inverts to the movement's colour. It does not
 * come back, and the slab it was holding drops. That is the whole argument of the model in
 * one image: the campaign does not win by being large, it wins by taking away the things
 * the regime is standing on.
 *
 * Fixed 1200×630 viewBox so a screenshot is legible at social-card size without cropping.
 */

import { useMemo } from 'react';
import type { DaySnapshot, RunLog } from 'walkout-engine';
import './NodeGraph.css';

const W = 1200;
const H = 630;

const SLAB_TOP = 28;
const SLAB_H = 52;
/**
 * The columns start well below the slab rather than tight against it. The loyalty figure
 * sits in that band, and the slab sinks by up to 26px as institutions give way — without
 * the clearance it slides over the numbers it is meant to be resting on.
 */
const COL_TOP = 150;
const COL_BOTTOM = 334;
const SECTOR_TOP = 412;
const SECTOR_H = 128;
const MARGIN = 44;
/** Below this, a sector's line to its institutions is drawn faintly: pressure, but not much. */
const LINK_VISIBLE_AT = 0.01;

function pct(value: number): string {
  const percent = value * 100;
  if (percent >= 10) return `${Math.round(percent)}%`;
  if (percent >= 1) return `${percent.toFixed(1)}%`;
  return `${percent.toFixed(1)}%`;
}

/** Split a label onto at most two lines without breaking words. */
function wrap(text: string, perLine: number): string[] {
  const words = text.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if (current && (current + ' ' + word).length > perLine) {
      lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
    if (lines.length === 1 && current.length > perLine) break;
  }
  if (current) lines.push(current);
  return lines.slice(0, 2);
}

interface Column {
  id: string;
  name: string;
  x: number;
  width: number;
  loyalty: number;
  defectedOn: number | null;
}

interface Node {
  id: string;
  name: string;
  x: number;
  width: number;
  participation: number;
  support: number;
  spent: number;
  linkedTo: string[];
}

export function NodeGraph({ run, day }: { run: RunLog; day: DaySnapshot }) {
  const columns = useMemo<Column[]>(() => {
    const pillars = day.pillars;
    const usable = W - MARGIN * 2;
    const gap = 14;
    const totalWeight = run.config.pillars.reduce((acc, p) => acc + p.weight, 0) || 1;
    const available = usable - gap * (pillars.length - 1);

    let x = MARGIN;
    return pillars.map((snapshot) => {
      const config = run.config.pillars.find((p) => p.id === snapshot.id);
      // Column width tracks how much of the state this institution actually carries, with
      // a floor so a small pillar is still a readable target rather than a sliver.
      const width = Math.max(46, (available * (config?.weight ?? 0)) / totalWeight);
      const column: Column = {
        id: snapshot.id,
        name: config?.name ?? snapshot.id,
        x,
        width,
        loyalty: snapshot.loyalty,
        defectedOn: snapshot.defectedOn,
      };
      x += width + gap;
      return column;
    });
  }, [run, day.pillars]);

  const nodes = useMemo<Node[]>(() => {
    const sectors = day.sectors;
    const usable = W - MARGIN * 2;
    const gap = 10;
    const available = usable - gap * (sectors.length - 1);
    const width = available / sectors.length;

    return sectors.map((snapshot, index) => {
      const config = run.config.sectors.find((s) => s.id === snapshot.id);
      return {
        id: snapshot.id,
        name: config?.name ?? snapshot.id,
        x: MARGIN + index * (width + gap),
        width,
        participation: snapshot.participation,
        support: snapshot.privateSupport,
        spent: snapshot.spent,
        linkedTo: run.config.pillars
          .filter((p) => p.linkedSectors.includes(snapshot.id))
          .map((p) => p.id),
      };
    });
  }, [run, day.sectors]);

  const columnById = useMemo(() => new Map(columns.map((c) => [c.id, c])), [columns]);

  // The slab sinks as the institutions holding it up give way.
  const sag = (1 - day.regime.capacity) * 26;
  const standing = columns.filter((c) => c.defectedOn === null).length;

  return (
    <svg
      className="graph"
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMidYMid meet"
      role="img"
      aria-label={`Day ${day.day}. ${standing} of ${columns.length} institutions still supporting the regime. ${pct(day.participation)} of the workforce out.`}
    >
      <defs>
        <pattern id="hatch" width="6" height="6" patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width="6" height="6" fill="var(--paper)" />
          <line x1="0" y1="0" x2="0" y2="6" stroke="var(--ink-black)" strokeWidth="1.5" />
        </pattern>
        {/* Sparse enough to read as texture rather than noise at this scale. */}
        <pattern id="dots" width="10" height="10" patternUnits="userSpaceOnUse">
          <rect width="10" height="10" fill="var(--paper)" />
          <circle cx="2.5" cy="2.5" r="1.3" fill="var(--paper-deep)" />
        </pattern>
      </defs>

      {/* --- Pressure lines: sectors to the institutions they sit under --------- */}
      <g className="graph__links">
        {nodes.map((node) =>
          node.linkedTo.map((pillarId) => {
            const column = columnById.get(pillarId);
            if (!column || node.participation < LINK_VISIBLE_AT) return null;
            const from = { x: node.x + node.width / 2, y: SECTOR_TOP };
            const to = { x: column.x + column.width / 2, y: COL_BOTTOM };
            // Straight, not curved. The band between the two rows is shallow and the
            // horizontal distances are large, so any curve here flattens into a sweeping
            // ribbon that crosses everything else on its way. Straight lines at the same
            // shallow angle read as ties carrying load, which is what they are.
            return (
              <line
                key={`${node.id}-${pillarId}`}
                className={`link${column.defectedOn !== null ? ' link--severed' : ''}`}
                x1={from.x}
                y1={from.y}
                x2={to.x}
                y2={to.y}
                strokeWidth={0.75 + Math.sqrt(node.participation) * 5}
                opacity={column.defectedOn !== null ? 0.1 : 0.12 + node.participation * 0.4}
              />
            );
          }),
        )}
      </g>

      {/* --- The regime ---------------------------------------------------------- */}
      <g className="graph__slab" style={{ transform: `translateY(${sag}px)` }}>
        <rect x={MARGIN - 14} y={SLAB_TOP} width={W - (MARGIN - 14) * 2} height={SLAB_H} fill="var(--ink-black)" />
        <text x={MARGIN} y={SLAB_TOP + 36} className="graph__slab-label">
          THE REGIME
        </text>
        <text x={W - MARGIN} y={SLAB_TOP + 36} className="graph__slab-figure" textAnchor="end">
          {pct(day.regime.capacity)} STILL ANSWERING
        </text>
      </g>

      {/* --- Pillars ------------------------------------------------------------- */}
      <g className="graph__columns">
        {columns.map((column) => {
          const gone = column.defectedOn !== null;
          const height = COL_BOTTOM - COL_TOP;
          const solid = gone ? 0 : Math.max(0, column.loyalty) * height;

          return (
            <g
              key={column.id}
              className={`column${gone ? ' column--gone' : ''}`}
              style={{ transformOrigin: `${column.x + column.width / 2}px ${COL_BOTTOM}px` }}
            >
              {/* The empty part of the column: what loyalty has already been lost. */}
              <rect
                x={column.x}
                y={COL_TOP}
                width={column.width}
                height={height}
                fill="url(#hatch)"
                stroke="var(--ink-black)"
                strokeWidth="3"
              />

              {gone ? (
                /* A column that has gone is not an empty box — it is masonry, still
                   standing in outline but split through and no longer carrying anything.
                   Filling it solid gives the cracks something to run across; leaving it
                   empty made them read as scribbles on a blank rectangle. */
                <>
                  <rect
                    className="column__solid"
                    x={column.x}
                    y={COL_TOP}
                    width={column.width}
                    height={height}
                    fill="var(--ink-red)"
                  />
                  <path
                    className="column__crack"
                    d={`M ${column.x} ${COL_TOP + height * 0.3}
                        l ${column.width * 0.34} ${height * 0.11}
                        l ${column.width * 0.22} ${-height * 0.14}
                        l ${column.width * 0.44} ${height * 0.17}`}
                  />
                  <path
                    className="column__crack"
                    d={`M ${column.x} ${COL_TOP + height * 0.66}
                        l ${column.width * 0.46} ${-height * 0.1}
                        l ${column.width * 0.22} ${height * 0.15}
                        l ${column.width * 0.32} ${-height * 0.07}`}
                  />
                  <path
                    className="column__crack column__crack--fine"
                    d={`M ${column.x + column.width * 0.5} ${COL_TOP + height * 0.41}
                        l ${-column.width * 0.12} ${height * 0.25}`}
                  />
                  <rect
                    x={column.x}
                    y={COL_TOP}
                    width={column.width}
                    height={height}
                    fill="none"
                    stroke="var(--ink-black)"
                    strokeWidth="3"
                  />
                </>
              ) : (
                /* What is still solid, filling from the footing up. */
                <rect
                  className="column__solid"
                  x={column.x}
                  y={COL_BOTTOM - solid}
                  width={column.width}
                  height={solid}
                  fill="var(--ink-black)"
                />
              )}

              <text
                className={`column__figure${gone ? ' column__figure--gone' : ''}`}
                x={column.x + column.width / 2}
                y={COL_TOP - 16}
                textAnchor="middle"
              >
                {gone ? `GONE d${column.defectedOn}` : pct(column.loyalty)}
              </text>

            </g>
          );
        })}
      </g>

      {/* Institution names sit outside the rotating group: a column that topples should
          not drag its own caption across its neighbour's. */}
      <g className="graph__column-labels">
        {columns.map((column) =>
          wrap(column.name, Math.max(10, Math.floor(column.width / 6.2))).map((line, index) => (
            <text
              key={`${column.id}-${line}`}
              className={`column__label${column.defectedOn !== null ? ' column__label--gone' : ''}`}
              x={column.x + column.width / 2}
              y={COL_BOTTOM + 26 + index * 14}
              textAnchor="middle"
            >
              {line}
            </text>
          )),
        )}
      </g>

      {/* --- Ground -------------------------------------------------------------- */}
      <line
        x1={MARGIN - 14}
        y1={COL_BOTTOM}
        x2={W - MARGIN + 14}
        y2={COL_BOTTOM}
        stroke="var(--ink-black)"
        strokeWidth="8"
      />

      {/* --- Sectors ------------------------------------------------------------- */}
      <g className="graph__nodes">
        {nodes.map((node) => {
          const fill = node.participation * SECTOR_H;
          const supportLine = SECTOR_TOP + SECTOR_H - node.support * SECTOR_H;
          const labels = wrap(node.name, Math.max(8, Math.floor(node.width / 5.6)));

          return (
            <g key={node.id} className="node">
              {/* The void: everything this sector could be doing and is not. */}
              <rect
                x={node.x}
                y={SECTOR_TOP}
                width={node.width}
                height={SECTOR_H}
                fill="url(#dots)"
                stroke="var(--ink-black)"
                strokeWidth="3"
              />
              {/* Participation, filling from the bottom. */}
              <rect
                className="node__fill"
                x={node.x}
                y={SECTOR_TOP + SECTOR_H - fill}
                width={node.width}
                height={fill}
                fill="var(--ink-red)"
              />
              {/* How far private support extends — the ceiling nobody has reached. */}
              <line
                className="node__support"
                x1={node.x}
                y1={supportLine}
                x2={node.x + node.width}
                y2={supportLine}
                stroke="var(--ink-blue)"
                strokeWidth="4"
              />
              {node.spent > 0.15 && (
                <rect
                  x={node.x}
                  y={SECTOR_TOP}
                  width={node.width}
                  height={node.spent * SECTOR_H}
                  fill="url(#hatch)"
                  opacity="0.85"
                />
              )}

              <text
                className="node__figure"
                x={node.x + node.width / 2}
                y={SECTOR_TOP - 10}
                textAnchor="middle"
              >
                {pct(node.participation)}
              </text>
              {labels.map((line, index) => (
                <text
                  key={line}
                  className="node__label"
                  x={node.x + node.width / 2}
                  y={SECTOR_TOP + SECTOR_H + 20 + index * 13}
                  textAnchor="middle"
                >
                  {line}
                </text>
              ))}
            </g>
          );
        })}
      </g>

      {/* --- Key ----------------------------------------------------------------- */}
      <g className="graph__key" transform={`translate(${MARGIN}, ${H - 22})`}>
        <rect x="0" y="-9" width="18" height="11" fill="var(--ink-red)" />
        <text x="24" y="0">OUT</text>
        <line x1="78" y1="-4" x2="96" y2="-4" stroke="var(--ink-blue)" strokeWidth="4" />
        <text x="102" y="0">PRIVATELY SUPPORT</text>
        <rect x="240" y="-9" width="18" height="11" fill="url(#dots)" stroke="var(--ink-black)" />
        <text x="264" y="0">THE GAP</text>
        <rect x="330" y="-9" width="18" height="11" fill="url(#hatch)" stroke="var(--ink-black)" />
        <text x="354" y="0">OUT OF MONEY</text>
      </g>
    </svg>
  );
}
